import { format } from "date-fns";

import { getOrganizationCarbonEmissions } from "Api/carbonEmissions/getOrganizationCarbonEmissions.request";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import { setOrganizationLoading } from "../carbonEmissions.reducer";
import { normalizeCarbonEmissionsData } from "../normalizeInput";

// Define a custom error payload type that includes organizationId
interface ErrorPayload {
  error: string;
  organizationId?: string;
  isNonRetryable?: boolean; // Flag for non-retryable errors like 404
}

const createErrorPayload = (error: unknown): ErrorPayload => {
  let errorMessage: string;
  let isNonRetryable = false;

  // Handle different error formats that might come from the API
  if (typeof error === "string") {
    // Text response body - this is what we expect for 404s
    errorMessage = error;

    // For text responses, check if it looks like a 404
    const lowerError = errorMessage.toLowerCase();
    isNonRetryable =
      lowerError.includes("not found") ||
      lowerError.includes("404") ||
      lowerError === "not found" ||
      lowerError === "404" ||
      // Common 404 messages
      lowerError.includes("resource not found") ||
      lowerError.includes("page not found") ||
      lowerError.includes("endpoint not found");
  } else if (error && typeof error === "object") {
    // JSON response body
    const errorObj = error as any;

    // Try to extract a meaningful error message
    errorMessage =
      errorObj.message ||
      errorObj.error ||
      errorObj.detail ||
      errorObj.description ||
      errorObj.title ||
      (errorObj.errors &&
        Array.isArray(errorObj.errors) &&
        errorObj.errors[0]?.message) ||
      JSON.stringify(error);

    // Check for status codes in the error object
    const statusCode = errorObj.status || errorObj.statusCode || errorObj.code;
    if ([400, 401, 403, 404].includes(statusCode)) {
      isNonRetryable = true;
    }

    // Also check the message content
    if (!isNonRetryable) {
      const lowerErrorMessage = errorMessage.toLowerCase();
      isNonRetryable =
        lowerErrorMessage.includes("404") ||
        lowerErrorMessage.includes("not found") ||
        lowerErrorMessage.includes("403") ||
        lowerErrorMessage.includes("forbidden") ||
        lowerErrorMessage.includes("401") ||
        lowerErrorMessage.includes("unauthorized") ||
        lowerErrorMessage.includes("400") ||
        lowerErrorMessage.includes("bad request");
    }
  } else {
    errorMessage = "An unknown error occurred";
  }

  return {
    error: errorMessage,
    isNonRetryable
  };
};

// Helper function to format dates for API
const formatDate = (date: Date): string => format(date, "yyyy-MM-dd");

export const fetchPreviousYearMonthlyCarbonEmissionsThunk = createAppAsyncThunk(
  "carbonEmissions/fetchPreviousYearMonthlyCarbonEmissions",
  async (organizationId: string, { rejectWithValue, dispatch }) => {
    try {
      // Set loading state using organizationId
      dispatch(
        setOrganizationLoading({
          organizationId,
          isLoading: true
        })
      );

      // Get dates for previous year but extend to beginning of current year
      const now = new Date();
      const previousYear = now.getFullYear() - 1;
      const currentYear = now.getFullYear();

      // Start date is Jan 1st of previous year
      const from = formatDate(new Date(previousYear, 0, 1));

      // End date is Jan 1st of current year (to include all of December)
      const to = formatDate(new Date(currentYear, 0, 1));

      const response = await getOrganizationCarbonEmissions({
        organizationId,
        from,
        to,
        interval: "month"
      });

      // Check if response has valid structure
      if (!response || !response.projects || response.projects.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("API returned empty or invalid carbon emissions data");
      }

      // Normalize the data with minimal transformation
      const normalizedData = normalizeCarbonEmissionsData(
        response,
        organizationId
      );

      // Return using organizationId for consistency
      return {
        organizationId,
        data: normalizedData
      };
    } catch (error) {
      // Set loading to false
      if (organizationId) {
        dispatch(
          setOrganizationLoading({
            organizationId,
            isLoading: false
          })
        );
      }

      const errorPayload = createErrorPayload(error);
      if (organizationId) {
        errorPayload.organizationId = organizationId;
      }

      return rejectWithValue(errorPayload);
    }
  }
);
